/**
 * Attendee-list parser for Marketing ▸ Webinars step 2.
 *
 * Every webinar platform exports a different file, and the operator should
 * not have to care. This takes whatever they paste — a Zoom/Teams CSV with a
 * header row, a tab-separated paste straight out of a spreadsheet, or just a
 * column of email addresses — and returns a normalized attendee list.
 *
 * It lives in `packages/shared` (not the server) on purpose: the SAME
 * function runs in the browser to preview "12 people found, 1 line skipped"
 * as the operator types, and on the server to do the actual import. One
 * implementation means the preview can never promise a result the import
 * then disagrees with.
 *
 * Rules, in order of how much they matter:
 *   - A row without a recognisable email is SKIPPED, never guessed at, and
 *     is echoed back verbatim so the operator can see what was dropped.
 *   - A header row is detected and dropped (it has no email in it anyway,
 *     but naming the columns lets us map them properly).
 *   - Unknown columns are ignored rather than being crammed into `name`.
 *   - Everyone is `joined: true` unless a column says otherwise, because
 *     the file the operator has in hand after a webinar is the ATTENDEE
 *     export. Registrant-only exports mark it explicitly.
 */
import type { ParsedAttendeeRow, WebinarAttendeeImportPreview } from "./webinars.js";

/** Deliberately permissive — we are recognising an address a human already
 * typed into a webinar platform, not validating one at signup. */
const EMAIL_RE = /[^\s,;<>"']+@[^\s,;<>"']+\.[^\s,;<>"']+/;

/** Header aliases → the field they fill. Lower-cased, punctuation-stripped
 * on both sides before comparing, so "First Name" and "first_name" match. */
const HEADER_ALIASES: Record<string, keyof ParsedAttendeeRow | "first" | "last"> = {
  name: "name",
  fullname: "name",
  attendee: "name",
  attendeename: "name",
  displayname: "name",
  participant: "name",
  participantname: "name",
  user: "name",
  firstname: "first",
  first: "first",
  givenname: "first",
  lastname: "last",
  last: "last",
  surname: "last",
  familyname: "last",
  email: "email",
  emailaddress: "email",
  mail: "email",
  useremail: "email",
  company: "company",
  organization: "company",
  organisation: "company",
  org: "company",
  account: "company",
  employer: "company",
  title: "job_title",
  jobtitle: "job_title",
  role: "job_title",
  position: "job_title",
  phone: "phone",
  phonenumber: "phone",
  mobile: "phone",
  tel: "phone",
  telephone: "phone",
  attended: "joined",
  joined: "joined",
  didattend: "joined",
  status: "joined",
  duration: "minutes_attended",
  minutes: "minutes_attended",
  timeinsession: "minutes_attended",
  durationminutes: "minutes_attended",
  attendanceduration: "minutes_attended",
};

function normalizeHeader(cell: string): string {
  return cell.toLowerCase().replace(/[^a-z]/g, "");
}

/** Split one line into cells. Tabs win over commas (a spreadsheet paste is
 * tab-separated and its cells routinely CONTAIN commas), and quoted cells
 * keep their commas. Semicolons are the European CSV separator. */
export function splitLine(line: string): string[] {
  if (line.includes("\t")) return line.split("\t").map(stripCell);
  const sep = !line.includes(",") && line.includes(";") ? ";" : ",";
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      // A doubled quote inside a quoted cell is a literal quote.
      if (quoted && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (ch === sep && !quoted) {
      out.push(stripCell(cur));
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(stripCell(cur));
  return out;
}

function stripCell(cell: string): string {
  const t = cell.trim();
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) {
    return t.slice(1, -1).replace(/""/g, '"').trim();
  }
  return t;
}

/** "No" / "false" / "0" / "did not attend" ⇒ did not join. Everything else
 * — including an empty cell — means joined, because the common case is an
 * attendee export where the column is a formality. */
function readJoined(cell: string): boolean {
  const v = cell.trim().toLowerCase();
  if (!v) return true;
  return !["no", "n", "false", "0", "absent", "did not attend", "not attended", "registered"].includes(
    v,
  );
}

/** Pull a whole number of minutes out of "42", "42 min", "01:05:00". */
export function readMinutes(cell: string): number {
  const v = cell.trim();
  if (!v) return 0;
  const clock = /^(\d+):(\d{2})(?::(\d{2}))?$/.exec(v);
  if (clock) {
    // HH:MM:SS when a seconds group is present, else MM:SS.
    const a = Number(clock[1]);
    const b = Number(clock[2]);
    return clock[3] !== undefined ? a * 60 + b : a;
  }
  const num = /(\d+)/.exec(v);
  return num ? Number(num[1]) : 0;
}

function emptyRow(): ParsedAttendeeRow {
  return {
    name: "",
    email: "",
    company: "",
    job_title: "",
    phone: "",
    joined: true,
    minutes_attended: 0,
  };
}

/**
 * Parse a pasted attendee list. Never throws — malformed input comes back
 * as `skipped` lines, because an operator pasting the wrong thing at 11pm
 * should see "we couldn't read these 3 lines", not a 500.
 */
export function parseAttendeeList(text: string): WebinarAttendeeImportPreview {
  const rows: ParsedAttendeeRow[] = [];
  const skipped: string[] = [];
  const seen = new Set<string>();

  const lines = String(text ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  // Column map, derived from a header row if there is one. Without a header
  // we fall back to positional guessing inside `readRow`.
  let columns: (keyof ParsedAttendeeRow | "first" | "last" | null)[] | null = null;

  for (const line of lines) {
    const cells = splitLine(line);

    // A header row is any line with no email that maps at least one known
    // column name. Lines with an email are always data, even if a column is
    // literally called "email" (some exports repeat the header mid-file).
    if (!EMAIL_RE.test(line)) {
      const mapped = cells.map((c) => HEADER_ALIASES[normalizeHeader(c)] ?? null);
      if (mapped.some((m) => m !== null)) {
        columns = mapped;
        continue;
      }
      skipped.push(line);
      continue;
    }

    const row = readRow(cells, columns);
    if (!row) {
      skipped.push(line);
      continue;
    }
    const key = row.email.toLowerCase();
    if (seen.has(key)) {
      // A duplicate is not an error worth showing — the same person joining
      // twice is normal (they dropped and rejoined). Keep the row that says
      // they joined, and the longer attendance.
      const prev = rows.find((r) => r.email.toLowerCase() === key)!;
      prev.joined = prev.joined || row.joined;
      prev.minutes_attended = Math.max(prev.minutes_attended, row.minutes_attended);
      if (!prev.name && row.name) prev.name = row.name;
      if (!prev.company && row.company) prev.company = row.company;
      if (!prev.job_title && row.job_title) prev.job_title = row.job_title;
      if (!prev.phone && row.phone) prev.phone = row.phone;
      continue;
    }
    seen.add(key);
    rows.push(row);
  }

  return { rows, skipped };
}

function readRow(
  cells: string[],
  columns: (keyof ParsedAttendeeRow | "first" | "last" | null)[] | null,
): ParsedAttendeeRow | null {
  const row = emptyRow();
  let first = "";
  let last = "";

  if (columns) {
    columns.forEach((field, i) => {
      const cell = cells[i] ?? "";
      if (!field || !cell) return;
      switch (field) {
        case "first":
          first = cell;
          break;
        case "last":
          last = cell;
          break;
        case "email": {
          const m = EMAIL_RE.exec(cell);
          if (m) row.email = m[0].toLowerCase();
          break;
        }
        case "joined":
          row.joined = readJoined(cell);
          break;
        case "minutes_attended":
          row.minutes_attended = readMinutes(cell);
          break;
        case "name":
        case "company":
        case "job_title":
        case "phone":
          row[field] = cell;
          break;
      }
    });
  }

  // No header (or the header lied): find the email anywhere in the row and
  // treat the first non-email, non-numeric cell before it as the name.
  if (!row.email) {
    for (const cell of cells) {
      const m = EMAIL_RE.exec(cell);
      if (m) {
        row.email = m[0].toLowerCase();
        break;
      }
    }
  }
  if (!row.email) return null;

  if (!row.name) {
    const joined = [first, last].filter(Boolean).join(" ").trim();
    row.name = joined || guessName(cells, row.email);
  }
  // No name in the file ⇒ no name. Guessing one from the address put
  // "Hello J Smits Work" in a 1,431-person thank-you (2026-10-07); crm-api now
  // greets by the CRM contact's name, or plain "Hello," when there is none.
  return row;
}

/** Positional fallback: the first cell that is neither the email nor a bare
 * number is very reliably the person's name in every export we've seen. */
function guessName(cells: string[], email: string): string {
  for (const cell of cells) {
    const t = cell.trim();
    if (!t) continue;
    if (t.toLowerCase() === email) continue;
    if (EMAIL_RE.test(t)) continue;
    if (/^[\d\s:.\-+()]+$/.test(t)) continue;
    return t;
  }
  return "";
}
